import { InfoListItem } from '@brightlayer-ui/react-components';
import React, { useState, useEffect } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import * as PXBColors from '@brightlayer-ui/colors';
import { Button, CircularProgress, ListItem, ListItemSecondaryAction, TextField, Typography } from '@mui/material';
import MailIcon from '@mui/icons-material/Mail';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import { UserBasicInfo, UserInfo } from '../../types/user';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { isValidChar } from '../../lib/user-helpers';
import { ONLY_NUMBER, US_CA_COUNTRY_CODE } from '../../constants';
import { cloneDeep } from 'lodash';

type EditUserProps = {
    userInfo: any;
    userDetailsInfo: UserInfo;
    onCancel: Function;
    onSubmit: Function;
    isDirty: Function;
};

export const EditUserDetails: React.FC<EditUserProps> = (props) => {
    const userInfo = props.userInfo;
    const user = props.userDetailsInfo;
    const { t } = useLanguageLocale();

    const [unmodifiedUserInfo, setUnmodifiedUserInfo] = useState<UserBasicInfo | null>(null);
    const [modifiedUserInfo, setModifiedUserInfo] = useState<UserBasicInfo | null>(null);

    const onChangePhoneNumber = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedUser: UserBasicInfo
    ): void => {
        if (e.target.value === '' || ONLY_NUMBER.test(e.target.value)) {
            const updatedPhone: UserBasicInfo = { ...modifiedUser };
            updatedPhone.phone = e.target.value;
            setModifiedUserInfo(updatedPhone);
        }
    };
    const onChangeFirstName = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedUser: UserBasicInfo
    ): void => {
        if (isValidChar(e.target.value)) {
            const updatedFirstName: UserBasicInfo = { ...modifiedUser };
            updatedFirstName.fName = e.target.value;
            setModifiedUserInfo(updatedFirstName);
        }
    };
    const onChangeLastName = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedUser: UserBasicInfo
    ): void => {
        if (isValidChar(e.target.value)) {
            const updatedLastName: UserBasicInfo = { ...modifiedUser };
            updatedLastName.lName = e.target.value;
            setModifiedUserInfo(updatedLastName);
        }
    };
    useEffect(() => {
        const phone =
            user.user.phone && user.user.phone.length > 10 ? user.user.phone.slice(-10) : user.user.phone || '';
        user.user.phone = phone;
        user.user.countryCode = US_CA_COUNTRY_CODE;
        const unmodifiedUser = cloneDeep(user.user);
        setUnmodifiedUserInfo(unmodifiedUser);
        setModifiedUserInfo(user.user);
    }, [user]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const isDataModified = JSON.stringify(unmodifiedUserInfo) !== JSON.stringify(modifiedUserInfo);
        props.isDirty(isDataModified);
    }, [unmodifiedUserInfo, modifiedUserInfo]); //eslint-disable-line react-hooks/exhaustive-deps

    const onCancel = (): void => {
        const isModified = JSON.stringify(unmodifiedUserInfo) !== JSON.stringify(modifiedUserInfo);
        if (!isModified) setModifiedUserInfo(unmodifiedUserInfo);
        props.onCancel(isModified);
    };

    const canSumbit: boolean =
        modifiedUserInfo &&
        modifiedUserInfo.fName !== '' &&
        modifiedUserInfo.lName !== '' &&
        modifiedUserInfo.phone !== '' &&
        modifiedUserInfo.phone &&
        modifiedUserInfo.phone.length === 10
            ? true
            : false;

    return (
        modifiedUserInfo && (
            <>
                <InfoListItem
                    divider={'full'}
                    data-testid="first-last name"
                    icon={<AccountCircleIcon />}
                    title=""
                    style={{ flex: 0 }}
                    leftComponent={
                        <>
                            <TextField
                                id="first-name"
                                style={{ width: '40%' }}
                                variant="standard"
                                label="First Name"
                                data-testid="editFirstName"
                                value={modifiedUserInfo.fName}
                                required
                                inputProps={{ maxLength: 50, 'data-testid': 'firstName' }}
                                onChange={(e): void => {
                                    onChangeFirstName(e, modifiedUserInfo);
                                }}
                            />
                            <TextField
                                id="last-name"
                                style={{ width: '40%', marginLeft: 20 }}
                                variant="standard"
                                label="Last Name"
                                data-testid="editLastName"
                                value={modifiedUserInfo.lName}
                                inputProps={{ maxLength: 50 }}
                                required
                                onChange={(e): void => {
                                    onChangeLastName(e, modifiedUserInfo);
                                }}
                            />
                        </>
                    }
                ></InfoListItem>
                <InfoListItem
                    divider={'full'}
                    data-testid="user-email"
                    icon={<MailIcon />}
                    title={modifiedUserInfo.email}
                    subtitle={t('USER_SETTINGS.EMAIL')}
                ></InfoListItem>
                <InfoListItem
                    divider={'full'}
                    data-testid="PhoneNo"
                    icon={<LocalPhoneIcon />}
                    title=""
                    leftComponent={
                        <>
                            <TextField
                                style={{ width: '5%', marginTop: 15 }}
                                variant="standard"
                                inputProps={{ 'data-testid': 'country-code', maxLength: 2 }}
                                value={modifiedUserInfo.countryCode}
                                disabled
                            />
                            <TextField
                                id="phoneNumber"
                                style={{ width: '30%', marginLeft: 20 }}
                                variant="standard"
                                label="PhoneNo"
                                required
                                data-testid="editPhone"
                                inputProps={{ 'data-testid': 'phone-number', maxLength: 10 }}
                                value={modifiedUserInfo.phone}
                                onChange={(e): void => onChangePhoneNumber(e, modifiedUserInfo)}
                            />
                        </>
                    }
                    rightComponent={<Typography variant="subtitle2">{t('USER_SETTINGS.PHONE_NO_TEXT')}</Typography>}
                ></InfoListItem>
                <ListItem style={{ height: '60px' }}>
                    <ListItemSecondaryAction>
                        <>
                            <Button
                                color="primary"
                                variant="outlined"
                                style={{ marginRight: 20 }}
                                data-testid="cancel-action"
                                onClick={(): void => {
                                    onCancel();
                                }}
                            >
                                Cancel
                            </Button>
                            {!userInfo.userDetailLoading && (
                                <Button
                                    disabled={!canSumbit}
                                    color="primary"
                                    variant="contained"
                                    data-testid="submit-action"
                                    onClick={(): void => {
                                        props.onSubmit(modifiedUserInfo);
                                    }}
                                >
                                    Submit Changes
                                </Button>
                            )}
                            {userInfo.userDetailLoading && (
                                <Button
                                    variant="contained"
                                    disabled
                                    startIcon={<CircularProgress size="1rem" style={{ color: 'white' }} />}
                                    style={{
                                        height: '40px',
                                        marginRight: 15,
                                        backgroundColor: PXBColors.blue[200],
                                        color: 'white',
                                    }}
                                >
                                    Submit Changes
                                </Button>
                            )}
                        </>
                    </ListItemSecondaryAction>
                </ListItem>
            </>
        )
    );
};
